import AppLoading from 'containers/App/AppLoading'
import AppTheme from 'containers/App/AppTheme'
import { EUserRole } from 'models/user.model'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { createInitialRegister } from 'services/auth.service'
import globalStore from 'store'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { ToRedirectFromRoute } from 'util/enums/toRedirectFromRouteEnum'
import { setSuperAdminContext } from 'ui/src/utils/superAdmin'
import companyService from 'services/company.service'
import { OnboardingStatus } from 'models/company.model'

const RedirectRoute = () => {
  const flags = useFlags()
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from
  const [loading, setLoading] = useState(true)
  const [loadedRegister, setLoadedRegister] = useState(false)
  const user = globalStore(state => state.user)
  const loadingUser = globalStore(state => state.loadingUser)
  const getUser = globalStore(state => state.getUser)
  const register = globalStore(state => state.register)
  const loadingRegister = globalStore(state => state.loadingRegister)
  const getRegister = globalStore(state => state.getRegister)
  const isFromSignIn = from === ToRedirectFromRoute.FROM_SIGN_IN

  const [searchParams] = useSearchParams()
  const [superAdminMode, setSuperAdminMode] = useState(false)
  const [ingested, setIngested] = useState(false)

  useEffect(() => {
    const companyId = searchParams.get('companyId')
    const encodeCompanyName = searchParams.get('companyName')

    const paramsExists = companyId && encodeCompanyName

    if (paramsExists) {
      const companyName = decodeURIComponent(encodeCompanyName)

      setSuperAdminContext({ enabled: true, companyId, companyName })
      setSuperAdminMode(true)
    }
    setIngested(true)
  }, [searchParams])

  const getAdminWelcomeRoute = useCallback(async () => {
    if (flags.dashboardV2) {
      const company = await companyService.getCompanyV2()
      if (company.onboardingStatus !== OnboardingStatus.COMPLETED) {
        return '/company/admin/welcome'
      }
    }

    if (isFromSignIn || superAdminMode) {
      return '/company/dashboard'
    }
    if (flags.dashboardV2) {
      return '/company/batch/welcome'
    }
    return '/company/welcome'
  }, [flags.dashboardV2, isFromSignIn, superAdminMode])

  const redirectBeforePreRegister = useCallback(
    async (hasCredits: boolean) => {
      if (!user) return

      let route = '/welcome'

      switch (user.role) {
        case EUserRole.ADMIN:
          // Check if batch scan exists?
          route = await getAdminWelcomeRoute()
          navigate(route)
          break
        case EUserRole.USER_B2C:
          if (hasCredits) {
            navigate('/userform')
          } else {
            navigate('/get-started')
          }
          break
        default:
          navigate('/welcome')
      }
    },
    [user, navigate, getAdminWelcomeRoute],
  )

  const redirectCompletedRegister = useCallback(() => {
    if (!user) return

    // Check if there's a pending redirect from before login
    const pendingRedirect = localStorage.getItem('pendingRedirect')
    if (pendingRedirect) {
      localStorage.removeItem('pendingRedirect')
      const url = new URL(pendingRedirect, window.location.origin)
      const dashboardType = url.searchParams.get('dashboard')

      switch (user.role) {
        case EUserRole.ADMIN:
          if (dashboardType === 'personal') {
            navigate('/dashboard')
          } else {
            navigate('/company/dashboard')
          }
          break
        default:
          if (dashboardType === 'company') {
            navigate('/company/dashboard')
          } else {
            navigate('/dashboard')
          }
      }
      return
    }

    const dashboardType = searchParams.get('dashboard')

    switch (user.role) {
      case EUserRole.ADMIN:
        if (dashboardType === 'personal') {
          navigate('/dashboard')
        } else {
          navigate('/company/dashboard')
        }
        break
      default:
        if (dashboardType === 'company') {
          navigate('/company/dashboard')
        } else {
          navigate('/dashboard')
        }
    }
  }, [user, navigate, searchParams])

  useEffect(() => {
    if (!user && !loadingUser) getUser()
  }, [])

  useEffect(() => {
    if (user) getRegister()
  }, [user, getRegister])

  useEffect(() => {
    if (!loadingRegister) setLoadedRegister(true)
  }, [loadingRegister])

  useEffect(() => {
    if (!loadingUser && !user) setLoading(false)
  }, [loadingUser, user])

  useEffect(() => {
    const fetchData = async () => {
      if (user && loadedRegister) {
        if (!register) {
          const company = user.role === EUserRole.USER_B2C ? null : user.companyId
          await createInitialRegister(user.name, user.email, user.phone, company)
          await getRegister()
          redirectBeforePreRegister(false)
        } else {
          if (register.status === 'completed') {
            redirectCompletedRegister()
          } else {
            redirectBeforePreRegister(register.stripe.credits > 0)
          }
        }
      }
    }
    fetchData()
  }, [
    loadedRegister,
    user,
    register,
    redirectBeforePreRegister,
    redirectCompletedRegister,
    getRegister,
  ])

  useEffect(() => {
    if (!loading && !user) {
      navigate('/signin', { state: { from: ToRedirectFromRoute.FROM_REDIRECT } })
    }
  }, [loading, user, navigate])

  if (loading || !ingested) {
    return (
      <AppTheme>
        <AppLoading />
      </AppTheme>
    )
  }

  if (!user) {
    // Save the current URL with all query params to localStorage before redirecting to signin
    const currentUrl = `${location.pathname}${location.search}`
    localStorage.setItem('pendingRedirect', currentUrl)
    return <Navigate to="/signin" />
  }

  return <Navigate to="/" />
}

export default RedirectRoute
